import React from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

// Human-friendly file-size formatter for the "downloaded X" banners.
function fmtSize(bytes) {
  if (bytes == null || Number.isNaN(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Renders the various result / error banners for BackupRestoreModal.
 * Every branch is a `result?.kind === "..."` check plus the error banner
 * on top. Extracted so the modal's JSX stays a straight top-to-bottom
 * read instead of a wall of conditional blocks.
 *
 * The historical "download" / "restore" kinds (biometrics-only JSON)
 * were removed on 2026-02-24 along with the backing endpoints — the
 * full-DB backup is a strict superset.
 */
export default function BackupResultBanners({ error, result }) {
  return (
    <>
      {error && (
        <div className="mt-4 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm flex items-start gap-2" data-testid="backup-error">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {result?.kind === "full-backup" && (
        <div className="mt-4 px-3 py-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 text-sm flex items-start gap-2" data-testid="full-backup-result">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
          <div>
            Downloaded <span className="font-mono-data">{result.filename}</span> ({fmtSize(result.size)}).
            Store this file somewhere safe — it contains your full medical database.
          </div>
        </div>
      )}

      {result?.kind === "full-backup-pdfs-progress" && (
        <div className="mt-4 px-3 py-3 rounded-lg bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 text-sm" data-testid="full-backup-pdfs-progress">
          <div className="flex items-center gap-2 font-display">
            <Loader2 className="animate-spin" size={14} />
            Downloading full backup + PDFs — current shard: <span className="font-mono-data">{result.step}</span>
          </div>
          {result.downloaded?.length > 0 && (
            <ul className="text-[10.5px] text-cyan-200/80 mt-1.5 ml-6 font-mono-data space-y-0.5">
              {result.downloaded.map((f, i) => (
                <li key={i}>✓ {f.filename} · {fmtSize(f.size)}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {result?.kind === "full-backup-pdfs" && (
        <div className="mt-4 px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm" data-testid="full-backup-pdfs-result">
          <div className="flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <div>
              Full disaster-recovery backup complete — {result.downloaded?.length || 0} files saved to your Downloads folder.
              <ul className="text-[10.5px] text-emerald-200/80 mt-1.5 font-mono-data space-y-0.5">
                {(result.downloaded || []).map((f, i) => (
                  <li key={i}>• {f.filename} · {fmtSize(f.size)}</li>
                ))}
              </ul>
              <div className="text-[10.5px] text-emerald-200/70 mt-1.5">
                Keep these together — restore reads the JSON; the ZIPs are for
                rebuilding the GridFS PDF archive if needed.
              </div>
            </div>
          </div>
        </div>
      )}

      {result?.kind === "restore-progress" && (
        <div className="mt-4 px-3 py-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-200 text-sm" data-testid="restore-progress">
          <div className="flex items-center gap-2 font-display">
            <Loader2 className="animate-spin" size={14} />
            {result.step === "safety-backup"
              ? "Step 1 / 2 — downloading safety backup to your computer…"
              : "Step 2 / 2 — restoring the uploaded backup into this environment…"}
          </div>
          {result.safetyBackupName && (
            <div className="text-[10px] text-slate-400 mt-1 ml-6 font-mono-data">
              ✓ Safety backup saved: <span className="text-emerald-300">{result.safetyBackupName}</span>
            </div>
          )}
        </div>
      )}

      {result?.kind === "full-restore" && (
        <div className="mt-4 space-y-2" data-testid="full-restore-result">
          <div className="px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <div>
              Full restore complete — backup from <span className="font-mono-data">{result.backup_created_at?.slice(0, 16)}</span> merged in.
              {result.safetyBackupName && (
                <div className="text-[10px] text-emerald-300/90 mt-1">
                  Safety net: pre-restore snapshot saved as <span className="font-mono-data">{result.safetyBackupName}</span>
                </div>
              )}
            </div>
          </div>
          <div className="border border-[#1E304B] rounded-lg overflow-hidden">
            <table className="w-full text-[12px]">
              <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#0B1627]">
                <tr>
                  <th className="text-left px-3 py-1.5">Collection</th>
                  <th className="text-right px-3 py-1.5">In backup</th>
                  <th className="text-right px-3 py-1.5">Inserted / Updated</th>
                  <th className="text-right px-3 py-1.5">Before → After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1E304B]/60">
                {Object.entries(result.counts || {}).map(([k, c]) => (
                  <tr key={k} className="hover:bg-[#0E1A2E]">
                    <td className="px-3 py-1.5 text-slate-100 capitalize">{k.replace(/_/g, " ")}</td>
                    <td className="px-3 py-1.5 text-right font-mono-data text-slate-300">{c.in_backup}</td>
                    <td className="px-3 py-1.5 text-right font-mono-data text-emerald-300">{c.inserted_or_updated}</td>
                    <td className="px-3 py-1.5 text-right font-mono-data text-slate-400">{c.before} → {c.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {result?.kind === "full-restore-zip" && (
        <div className="mt-4 space-y-2" data-testid="full-restore-zip-result">
          <div className="px-3 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm flex items-start gap-2">
            <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
            <div>
              ZIP restore complete — mode <span className="font-mono-data">{result.mode}</span>,
              {" "}{result.pdf_total} PDF file{result.pdf_total === 1 ? "" : "s"} processed.
              {result.safetyBackupName && (
                <div className="text-[10px] text-emerald-300/90 mt-1">
                  Safety net: pre-restore snapshot saved as <span className="font-mono-data">{result.safetyBackupName}</span>
                </div>
              )}
            </div>
          </div>
          {result.pdf_restore && Object.keys(result.pdf_restore).length > 0 && (
            <div className="border border-[#1E304B] rounded-lg overflow-hidden">
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#0B1627]">
                  <tr>
                    <th className="text-left px-3 py-1.5">GridFS bucket</th>
                    <th className="text-right px-3 py-1.5">In ZIP</th>
                    <th className="text-right px-3 py-1.5 text-cyan-400">Inserted</th>
                    <th className="text-right px-3 py-1.5 text-slate-400">Already present</th>
                    <th className="text-right px-3 py-1.5 text-rose-400">Bad ObjectId</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E304B]/60">
                  {Object.entries(result.pdf_restore).map(([bucket, c]) => (
                    <tr key={bucket} className="hover:bg-[#0E1A2E]">
                      <td className="px-3 py-1.5 text-slate-100 font-mono-data">{bucket}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-slate-300">{c.in_zip}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-cyan-300">{c.inserted}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-slate-400">{c.already_present}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-rose-300">{c.bad_oid}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.json_restore && (
            <details className="border border-[#1E304B] rounded">
              <summary className="px-3 py-1.5 cursor-pointer text-[11px] uppercase tracking-widest text-cyan-300">
                JSON restore counts ({Object.keys(result.json_restore.counts || {}).length} collections)
              </summary>
              <table className="w-full text-[12px]">
                <thead className="text-[10px] uppercase tracking-wider text-slate-500 bg-[#0B1627]">
                  <tr>
                    <th className="text-left px-3 py-1.5">Collection</th>
                    <th className="text-right px-3 py-1.5">In backup</th>
                    <th className="text-right px-3 py-1.5">Ins / Upd</th>
                    <th className="text-right px-3 py-1.5">Before → After</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#1E304B]/60">
                  {Object.entries(result.json_restore.counts || {}).map(([k, c]) => (
                    <tr key={k} className="hover:bg-[#0E1A2E]">
                      <td className="px-3 py-1.5 text-slate-100 capitalize">{k.replace(/_/g, " ")}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-slate-300">{c.in_backup}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-emerald-300">{c.inserted_or_updated}</td>
                      <td className="px-3 py-1.5 text-right font-mono-data text-slate-400">{c.before} → {c.after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}

    </>
  );
}
